import { Link } from "react-router-dom";
import Navbar from "../../Menu/Navbar";
import Footer from "../Sections/Footer";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../apis/store";
import { useEffect, useState } from "react";
import { toGetNews } from "../../../apis/actions/news.actions";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { TbArrowUpRight, TbCalendar } from "react-icons/tb";
import "moment/locale/ar";

type NewsItem = {
  id: number;
  name: string;
  summary?: string;
  details?: string;
  image_1920?: string;
  date?: string;
  tag_ids?: { name: string }[];
};

const plainText = (html?: string) => {
  if (!html) return "";
  const element = document.createElement("div");
  element.innerHTML = html;
  return element.textContent?.replace(/\s+/g, " ").trim() || "";
};

const NewsList = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { list, status } = useSelector((state: RootState) => state.news.news);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const news: NewsItem[] = Array.isArray(list) ? list : [];
  const tags = Array.from(new Set(news.flatMap((item) => item.tag_ids?.map((tag) => tag.name) || [])));
  const filtered = activeTag ? news.filter((item) => item.tag_ids?.some((tag) => tag.name === activeTag)) : news;
  const featured = filtered[0];
  const remaining = filtered.slice(1);
  const locale = i18n.language === "ar" ? "ar" : "en";

  useEffect(() => {
    dispatch(toGetNews(i18n.language));
  }, [dispatch, i18n.language]);

  const newsDate = (date?: string) => date && moment(date).isValid()
    ? moment(date).locale(locale).format("D MMMM YYYY")
    : null;

  const imageFallback = (event: React.SyntheticEvent<HTMLImageElement>) => {
    if (!event.currentTarget.src.endsWith("/assets/imgs/page-header/banner5.png")) {
      event.currentTarget.src = "/assets/imgs/page-header/banner5.png";
    }
  };

  return (
    <>
      <Navbar type="internal" />
      <main className="ac-news-list" dir={i18n.dir()}>
        <header className="ac-news-list__masthead">
          <div className="container">
            <nav className="ac-news-detail__breadcrumbs" aria-label={t("newsDetail.breadcrumb")}>
              <Link to="/">{t("menu.home")}</Link><span aria-hidden="true">/</span><span>{t("menu.news")}</span>
            </nav>
            <div className="ac-news-list__intro">
              <span className="ac-news-list__eyebrow">{t("newsPage.eyebrow")}</span>
              <h1>{t("menu.news")}</h1>
              <p>{t("newsPage.intro")}</p>
            </div>
          </div>
        </header>

        <div className="container ac-news-list__body">
          {tags.length > 0 && (
            <div className="ac-news-list__filters" aria-label={t("newsPage.filterLabel")}>
              <button type="button" className={!activeTag ? "is-active" : ""} onClick={() => setActiveTag(null)} aria-pressed={!activeTag}>{t("newsPage.all")}</button>
              {tags.map((tag) => <button type="button" key={tag} className={activeTag === tag ? "is-active" : ""} onClick={() => setActiveTag(tag)} aria-pressed={activeTag === tag}>{tag}</button>)}
            </div>
          )}

          {featured ? (
            <>
              <article className="ac-news-list__featured">
                <Link to={`/news-detail?id=${featured.id}`} state={featured} className="ac-news-list__featured-image" aria-label={featured.name}>
                  <img src={featured.image_1920 || "/assets/imgs/page-header/banner5.png"} alt="" onError={imageFallback} />
                </Link>
                <div className="ac-news-list__featured-copy">
                  <div className="ac-news-detail__meta">
                    {featured.tag_ids?.[0]?.name && <span className="ac-news-detail__tag">{featured.tag_ids[0].name}</span>}
                    {newsDate(featured.date) && <span className="ac-news-detail__date"><TbCalendar aria-hidden="true" />{newsDate(featured.date)}</span>}
                  </div>
                  <h2><Link to={`/news-detail?id=${featured.id}`} state={featured}>{featured.name}</Link></h2>
                  {featured.summary && <p>{plainText(featured.summary)}</p>}
                  <Link to={`/news-detail?id=${featured.id}`} state={featured} className="ac-news-list__read">{t("newsPage.read")}<TbArrowUpRight aria-hidden="true" /></Link>
                </div>
              </article>

              {remaining.length > 0 && (
                <section className="ac-news-list__more" aria-labelledby="more-news-title">
                  <div className="ac-news-list__section-heading">
                    <h2 id="more-news-title">{t("newsPage.more")}</h2>
                  </div>
                  <div className="ac-news-list__grid">
                    {remaining.map((item, index) => (
                      <article className="ac-news-list__card" key={item.id} style={{ animationDelay: `${Math.min(index, 5) * 80}ms` }}>
                        <Link to={`/news-detail?id=${item.id}`} state={item} className="ac-news-list__card-image" aria-label={item.name}>
                          <img src={item.image_1920 || "/assets/imgs/page-header/banner5.png"} alt="" loading="lazy" onError={imageFallback} />
                        </Link>
                        <div className="ac-news-list__card-body">
                          <div className="ac-news-list__card-meta">
                            {item.tag_ids?.[0]?.name && <span>{item.tag_ids[0].name}</span>}
                            {newsDate(item.date) && <time>{newsDate(item.date)}</time>}
                          </div>
                          <h3><Link to={`/news-detail?id=${item.id}`} state={item}>{item.name}</Link></h3>
                          {item.summary && <p>{plainText(item.summary)}</p>}
                          <Link to={`/news-detail?id=${item.id}`} state={item} className="ac-news-list__card-link">{t("newsPage.read")}<TbArrowUpRight aria-hidden="true" /></Link>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              )}
            </>
          ) : (
            <div className="ac-news-list__empty" role="status">
              {status === "idle" || status === "" ? t("btn.loading") : status === "rejected" ? t("newsPage.loadError") : t("newsPage.empty")}
            </div>
          )}
        </div>
      </main>
      <Footer type="external" />
    </>
  );
};

export default NewsList;
